import { ComponentProps } from "react";
import { useWindowSize } from "@hooks/useWindowSize";
import { ImagePresets } from "@pages/Article/components/ArticleThumbnail/utils/enums";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { cn } from "@util/helpers";

type TemplateIProps = {
  /** The list of card props */
  cardProps: ComponentProps<typeof Card>[];
  /** Additional class name for the container */
  className?: string;
};

/**
 * Template I component for Singapore Budget, BTSG60 Top Stories
 * Figma: https://www.figma.com/design/FZiuxiCLQaNXAJmLRtAAY9/BUDGET-2025--WEB---APP---NL-?node-id=8518-386590&t=NrMkoRPajprK1OWW-4
 */
const TemplateI = ({ cardProps, className }: TemplateIProps) => {
  const { isScreenLG } = useWindowSize();

  const mainVariation: CardVariations = {
    image: {
      position: "left",
      width: "w-full lg:w-1/2",
      aspectRatio: "aspect-3x2",
      orientation: "landscape",
      filters: { w: ImagePresets.MEDIUM, dpr: 1, f: "webp" },
    },
    content: {
      width: "w-full lg:w-1/2",
      extraClass: cn(
        "bg-gray-850 text-center p-5 lg:pt-0 lg:pb-0 lg:pr-0 lg:pl-4 lg:bg-transparent lg:text-left"
      ),
      blurb: { extraClass: "text-gray-850", wrapperClassName: cn("lg:my-2") },
      updatedTimeColor: cn("text-gray-850"),
      updatedTimeExtraClass: cn("font-poppins text-7xs"),
      updatedTimeWrapperClass: cn("pt-2 lg:mt-auto"),
    },
    title: {
      size: "text-4xl",
      color: "text-white lg:text-gray-850",
      extraClass: cn("font-bold font-lct"),
    },
    kicker: {
      color: cn("text-white lg:text-black"),
      extraClass: cn("font-poppins text-4xs font-light leading-6"),
    },
  };

  const otherVariation: CardVariations = {
    title: {
      color: "text-gray-850",
      size: "text-base lg:text-lg",
      extraClass: cn("font-bold font-lct"),
    },
    content: {
      width: "w-full",
      updatedTimeColor: cn("text-gray-850"),
      updatedTimeExtraClass: cn("font-poppins text-7xs"),
      updatedTimeWrapperClass: cn("pt-2 lg:mt-auto"),
    },
  };

  return (
    <div
      data-testid="singapore-budget-top-stories-component"
      className={cn("mb-10 grid lg:grid-cols-2", className)}
    >
      {cardProps.map((card, index) => {
        return (
          <div
            className={cn("col-span-1 border-b border-gray-175 py-3", {
              "lg:col-span-2": index === 0,
              "border-b-0":
                index === cardProps.length - 1 ||
                index === cardProps.length - 2,
            })}
            key={`budget-top-stories-${card.id}`}
          >
            <Card
              {...card}
              variations={index === 0 ? mainVariation : otherVariation}
              thumbnail={isScreenLG && index === 0 ? card.thumbnail : undefined}
              defaultImage={
                isScreenLG && index === 0 ? card.defaultImage : undefined
              }
              className={cn("h-full border-gray-175", {
                "lg:border-r lg:pr-3": [1, 3, 5].includes(index),
                "lg:pl-3": [2, 4, 6].includes(index),
              })}
            />
          </div>
        );
      })}
    </div>
  );
};

export default TemplateI;
